import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GetMySubscriptionAction } from '../../../redux/actions/subscriptionAction.js';
import { GetAllTeamsAction } from '../../../redux/actions/teamActions.js';
import { useHistory } from 'react-router-dom';
import mainImg from '../assets/dashboard.png';
import "./dashboard.css";
import { getLicenseTeamLogs, getGameResultCount } from '../../services/gameLog.services.js';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StatsChart from '../../../components/dashboardChart/dashboardChart.jsx';
import UserSidebar from '../../../components/userSiderbar/userSidebar.jsx';
import { GeneratePlayerStats } from '../gamelogdetails/helper.js';

const UserDashboard = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const operatorTeam = useSelector(state => state.team.selectedTeam);
  const user = useSelector(state => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [playerStats, setPlayerStats] = useState({});
  const [topPerformers, setTopPerformers] = useState({});
  const [seasonStatsLoading, setSeasonStatsLoading] = useState(false);
  const [gameData, setGameData] = useState([]);
  const [opponentShotOGTotal, setOpponentShotOGTotal] = useState(0);

  const [countData, setCountData] = useState({
    totalGames: 0,
    wins: 0,
    losses: 0
  });
  const normalizeName = (name) =>
    name?.replace(/\s+/g, " ").trim();


const formatMinutesToMMSS = (minutes = 0) => {
  const totalSeconds = Math.floor(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

  // Convert playerStatsMap to array
  const allPlayers = Object.values(playerStats);
  const penaltiesMap = {};
  // Calculate team totals for specific stats
  const teamTotals = {
    goals: allPlayers.reduce((sum, p) => sum + (p.goals || 0), 0),
    assists: allPlayers.reduce((sum, p) => sum + (p.assists || 0), 0),
    groundBall: allPlayers.reduce((sum, p) => sum + (p.groundBall || 0), 0),
    causedTurnover: allPlayers.reduce((sum, p) => sum + (p.causedTurnover || 0), 0),
    shotOG: allPlayers.reduce((sum, p) => sum + (p.shotOG || 0), 0),
    shotAttempts: allPlayers.reduce((sum, p) => sum + (p.shotAttempts || 0), 0),
    turnOver: allPlayers.reduce((sum, p) => sum + (p.turnOver || 0), 0),
    clears: allPlayers.reduce((sum, p) => sum + (p.clears || 0), 0),
    saves: allPlayers.reduce((sum, p) => sum + (p.saves || 0), 0),
    faceoffsWon: allPlayers.reduce((sum, p) => sum + (p.faceoffsWon || 0), 0),
    foffsAttempts: allPlayers.reduce((sum, p) => sum + (p.foffsAttempts || 0), 0),
  };
  teamTotals.scoringAccuracy = allPlayers.reduce((sum, p) => sum + (p.goals || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.shotAttempts || 0), 0), 1);
  teamTotals.shootingAccuracy = allPlayers.reduce((sum, p) => sum + (p.shotOG || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.shotAttempts || 0), 0), 1);
  teamTotals.faceoffWinPct = allPlayers.reduce((sum, p) => sum + (p.faceoffsWon || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.foffsAttempts || 0), 0), 1);
  teamTotals.savePct = opponentShotOGTotal > 0
    ? (teamTotals.saves / opponentShotOGTotal)
    : 0;

  teamTotals.clearPct = allPlayers.reduce((sum, p) => sum + (p.clearsSuccessfull || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.clears || 0), 0), 1);
  teamTotals.controlledPct = allPlayers.reduce((sum, p) => sum + (p.savesControlled || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.saves || 0), 0), 1);
  teamTotals.notControlledPct = allPlayers.reduce((sum, p) => sum + (p.savesNotControlled || 0), 0)
    / Math.max(allPlayers.reduce((sum, p) => sum + (p.saves || 0), 0), 1);

  const aggregatePlayerStats = async () => {
    try {
      setSeasonStatsLoading(true);
      const response = await getLicenseTeamLogs(operatorTeam?.teamId._id, user?.token);
      const allGames = response.data;

      const playerStatsMap = {};
      const teamId = operatorTeam?.teamId._id;

      let totalOpponentShotOG = 0;

      // Process each game
      for (const game of allGames) {

        const { teamAplayerStats, teamBplayerStats } = GeneratePlayerStats(game);

        // console.log("FULL ACTIVITY LOG:", game.activityLog);

        // Our team stats
        const ourTeamStats =
          game.teamA.teamId === teamId ? teamAplayerStats :
            game.teamB.teamId === teamId ? teamBplayerStats : [];

        // Opponent stats
        const opponentTeamStats =
          game.teamA.teamId === teamId ? teamBplayerStats :
            game.teamB.teamId === teamId ? teamAplayerStats : [];

        // Accumulate opponent Shot On Goal
        opponentTeamStats.forEach(p => {
          totalOpponentShotOG += (p.shotOG || 0);
        });

        // Aggregate stats for our players
        ourTeamStats.forEach(player => {
          if (!player.player || !player.jersey) return;

          const playerKey = String(player.jersey);

          if (!playerStatsMap[playerKey]) {
            playerStatsMap[playerKey] = {
              name: normalizeName(player.player),
              jersey: player.jersey,
              gamesPlayed: 0,
              goals: 0,
              assists: 0,
              shotAttempts: 0,
              shotOG: 0,
              groundBall: 0,
              causedTurnover: 0,
              turnOver: 0,
              takeAway: 0,
              interceptions: 0,
              unforcedErrors: 0,
              foffsAttempts: 0,
              faceoffsWon: 0,
              faceoffsViolation: 0,
              faceoffGB: 0,
              goaliePlayedTime: 0,
              saves: 0,
              shotsConceded: 0,
              shotsOnNet: 0,
              goalsAgainst: 0,
              won: 0,
              lost: 0,
              tie: 0,
              clears: 0,
              clearsSuccessfull: 0,
              clearFailed: 0,
              savesControlled: 0,
              savesNotControlled: 0,
              shotOnGoal: 0
            };
          }

          else {
            const existingName = playerStatsMap[playerKey].name || "";
            const incomingName = normalizeName(player.player) || "";

            if (incomingName.length > existingName.length) {
              playerStatsMap[playerKey].name = incomingName;
            }
          }
          // Increment games played
          playerStatsMap[playerKey].gamesPlayed += 1;

          // Add numeric stats
          const stats = playerStatsMap[playerKey];
          Object.keys(stats).forEach(stat => {
            if (
              stat !== 'name' &&
              stat !== 'jersey' &&
              stat !== 'gamesPlayed' &&
              player[stat] !== undefined
            ) {
              stats[stat] += player[stat] || 0;
            }
          });
        });
        (game.activityLog || [])
          .filter(item => item.cat === "Penalty" && item.player && item.pTime)
          .forEach(penalty => {
            // console.log("Penalty Entry:", penalty);
            const key = `${penalty.player}-${penalty.jersey}`;

            if (!penaltiesMap[key]) {
              penaltiesMap[key] = {
                player: penalty.player || penalty.playerName || penalty.name,
                jersey: penalty.jersey,
                team: penalty.team,
                count: 0,
                totalTime: 0,
              };
            }

            penaltiesMap[key].count += 1;

            const timeValue = Number(penalty.time) || 0;
            penaltiesMap[key].totalTime += timeValue;


            penaltiesMap[key].totalTime += timeValue;
          });
      }

      setOpponentShotOGTotal(totalOpponentShotOG);
      setPlayerStats(playerStatsMap);
      calculateTopPerformers(playerStatsMap, totalOpponentShotOG, penaltiesMap);

    } catch (error) {
      console.log("ERROR aggregating player stats", error.message);
    } finally {
      setSeasonStatsLoading(false);
    }
  };

  // Calculate top performers from aggregated stats
  const calculateTopPerformers = (statsMap, opponentShotOGTotal) => {
    const players = Object.values(statsMap);

    players.forEach(player => {
      console.log(`Player: ${formatPlayerName(player.name)}, Goals: ${player.goals}, ShotAttempts: ${player.shotAttempts}, 
      Scoring Accuracy: ${player.goals / player.shotAttempts}`);
    });

    // Calculate derived stats for each player
    const playersWithDerivedStats = players.map(player => ({
      ...player,
      // Scoring accuracy: goals / shotAttempts
      scoringAccuracy: player.shotAttempts > 0 ? player.goals / player.shotAttempts : 0,
      // ... rest of the code
    }));

    const scoringAccuracyTop = playersWithDerivedStats
      .filter(p => p.shotAttempts > 0)
      .sort((a, b) => b.scoringAccuracy - a.scoringAccuracy)
      .slice(0, 3);

    scoringAccuracyTop.forEach(player => {
      console.log(`Top Player: ${formatPlayerName(player.name)}, Goals: ${player.goals}, 
      ShotAttempts: ${player.shotAttempts}, Accuracy: ${player.scoringAccuracy}`);
    });
    const penaltiesArray = Object.values(penaltiesMap);
    const topPerformers = {
      goals: [...players].sort((a, b) => b.goals - a.goals).slice(0, 3),
      assists: [...players].sort((a, b) => b.assists - a.assists).slice(0, 3),
      shotOG: [...players].sort((a, b) => b.shotOG - a.shotOG).slice(0, 3),
      causedTurnover: [...players].sort((a, b) => b.causedTurnover - a.causedTurnover).slice(0, 3),
      groundBall: [...players].sort((a, b) => b.groundBall - a.groundBall).slice(0, 3),

      penalties: penaltiesArray
        .sort((a, b) => b.count - a.count)
        .slice(0, 3),

      // Penalties by total time
      penaltyTime: penaltiesArray
        .sort((a, b) => b.totalTime - a.totalTime)
        .slice(0, 3),

      // Team totals
      teamTotals: {
        penalties: penaltiesArray.reduce((sum, p) => sum + p.count, 0),
        penaltyTime: penaltiesArray.reduce((sum, p) => sum + p.totalTime, 0)
      },

      // NEW: Total Shots
      shotAttempts: [...players].sort((a, b) => b.shotAttempts - a.shotAttempts).slice(0, 3),

      // NEW: Scoring Accuracy (goals / shotAttempts)
      scoringAccuracy: scoringAccuracyTop,

      // NEW: Shooting Accuracy ((goals + shotOG - saves) / shotAttempts)
      shootingAccuracy: players
        .filter(p => p.shotAttempts > 0)
        .map(p => ({
          ...p,
          shootingAccuracy: (p.goals) / p.shotAttempts
        }))
        .sort((a, b) => b.shootingAccuracy - a.shootingAccuracy)
        .slice(0, 3),


      // NEW Faceoff performance (faceoffsWon/foffsAttempts)
      faceoffWinPct: players
        .filter(p => p.foffsAttempts > 0)
        .map(p => ({
          ...p,
          winPct: p.faceoffsWon / p.foffsAttempts
        }))
        .sort((a, b) => b.winPct - a.winPct)
        .slice(0, 3),

      saves: players
        .filter(p => p.saves > 0)
        .map(p => ({
          ...p,
          savePct: opponentShotOGTotal > 0 ? (p.saves / opponentShotOGTotal) : 0
        }))
        .sort((a, b) => b.savePct - a.savePct)
        .slice(0, 3),



      // NEW Turnovers
      turnOver: [...players].sort((a, b) => b.turnOver - a.turnOver).slice(0, 3),

      // NEW Clears clearSuccess/attempts
      clears: players
        .filter(p => p.clears > 0) // only players with at least 1 clear attempt
        .map(p => ({
          ...p,
          clearPct: p.clearsSuccessfull / p.clears
        }))
        .sort((a, b) => b.clearPct - a.clearPct)
        .slice(0, 3),

      // NEW savesControlled / saves
      savesControlled: players
        .filter(p => p.saves > 0 && p.savesControlled > 0)
        .map(p => ({
          ...p,
          controlledPct: p.savesControlled / p.saves
        }))
        .sort((a, b) => b.controlledPct - a.controlledPct)
        .slice(0, 3),

      // NEW savesNotControlled / saves
      savesNotControlled: players
        .filter(p => p.saves > 0 && p.savesNotControlled > 0)
        .map(p => ({
          ...p,
          notControlledPct: p.savesNotControlled / p.saves
        }))
        .sort((a, b) => b.notControlledPct - a.notControlledPct)
        .slice(0, 3)
    };

    setTopPerformers(topPerformers);
  };
  useEffect(() => {
    if (operatorTeam?.teamId._id && user?.token) {
      fetchScreenData();
      aggregatePlayerStats();
    }
  }, [operatorTeam?.teamId._id, user?.token]);

  const formatPlayerName = (name) => {
  if (!name) return "";

  const cleaned = name.replace(/\s+/g, " ").trim();
  const parts = cleaned.split(" ");

  if (parts.length === 1) {
    // Single name fallback
    return parts[0];
  }

  const firstName = parts[0];
  const lastName = parts[parts.length - 1];

  return `${lastName}, ${firstName.charAt(0)}`;
};



  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  useEffect(() => {
    if (operatorTeam?.subscriptionType.subscriptionName === 'Free trial(Clock only)' || operatorTeam?.subscriptionType.subscriptionName === 'Free trial(Clock stats)') {
      setIsAvailable(operatorTeam?.timesUsed <= 0);
    } else if (operatorTeam?.subscriptionType.subscriptionName === 'times allowed(stats)' || operatorTeam?.subscriptionType.subscriptionName === 'times allowed(only)') {
      setIsAvailable(operatorTeam?.timesUsed < operatorTeam?.timesAllowed);
    } else {
      setIsAvailable(Number(operatorTeam?.endTime) - Date.now() > 0);
    }
  }, [operatorTeam]);

  useEffect(() => {
    if (user) {
      dispatch(GetMySubscriptionAction({ userId: user._id }, user?.token));
    }
    dispatch(GetAllTeamsAction(user?.token));
  }, [user, dispatch]);

  const handleGetAllGames = async () => {
    try {
      setLoading(true);
      const response = await getLicenseTeamLogs(operatorTeam?.teamId._id, user?.token);
      // console.log("Logs response:", response);
      setGameData(response.data);
    } catch (error) {
      console.log("ERROR in getLicenseTeamLogs", error.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGetGameResultCount = async () => {
    try {
      setCountLoading(true);
      const response = await getGameResultCount(operatorTeam?.teamId._id, user?.token);

      const wins = response.data.overall.won || 0;
      const losses = response.data.overall.lost || 0;
      const ties = response.data.overall.tied || 0;

      const totalGames = wins + losses + ties;

      setCountData({
        totalGames,
        wins,
        losses,
        ties,
      });
    } catch (error) {
      console.log("ERROR in getGameResultCount", error.message);
      setError(true);
    } finally {
      setCountLoading(false);
    }
  };

  const fetchScreenData = async () => {
    await handleGetGameResultCount();
    await handleGetAllGames();
  };

  useEffect(() => {
    if (operatorTeam?.teamId._id && user?.token) {
      fetchScreenData();
    }
  }, [operatorTeam?.teamId._id, user?.token]);

  return (
    <div className="dashboardContainer">
      <UserSidebar />
      <div className="mainContent">
        <div className="dashboardHeader">
          {/* <div className="dashboardImage">
            <img src={mainImg} className="headerImageDashboard" alt="laxstat clock" />
          </div> */}
          {/* <h1 className="userTeamNameMain">Welcome, <span style={{ color: '#a0a004' }}>{operatorTeam?.teamId?.teamName}</span></h1> */}
          <p className="teamSubtitle">SEASON PERFOMANCE DASHBOARD</p>
        </div>

        <div className="statsContainer">
          <div className="statCard">
            <div className="statIcon gamesPlayed">
              <SportsIcon sx={{ color: '#fff', fontSize: '30px' }} />
            </div>
            <div className="statContent">
              <h3 className="statNumber">{countData.totalGames || 0}</h3>
              <p className="statLabel">Games Played</p>
            </div>
          </div>

          {/* Games Won */}
          <div className="statCard">
            <div className="statIcon gamesWon">
              <EmojiEventsIcon sx={{ color: '#fff', fontSize: '30px' }} />
            </div>
            <div className="statContent">
              <h3 className="statNumber">{countData.wins || 0}</h3>
              <p className="statLabel">Games Won</p>
            </div>
          </div>

          {/* Games Lost */}
          <div className="statCard">
            <div className="statIcon gamesLost">
              <CancelIcon sx={{ color: '#fff', fontSize: '30px' }} />
            </div>
            <div className="statContent">
              <h3 className="statNumber">{countData.losses || 0}</h3>
              <p className="statLabel">Games Lost</p>
            </div>
          </div>

          {/* Win Rate */}
          <div className="statCard">
            <div className="statIcon winRate">
              <TrendingUpIcon sx={{ color: '#fff', fontSize: '30px' }} />
            </div>
            <div className="statContent">
              <h3 className="statNumber">
                {countData.totalGames > 0
                  ? `${Math.round((countData.wins / countData.totalGames) * 100)}%`
                  : '0%'}
              </h3>
              <p className="statLabel">Win Rate</p>
            </div>
          </div>

        </div>

        <div className="seasonStatsSection">
          <h2>Season Player/Team Statistics</h2>

          {seasonStatsLoading ? (
            <div className="loading">Loading player statistics...</div>
          ) : Object.keys(playerStats).length > 0 ? (
            <>
              {/* Top Performers */}
              <div className="topPerformers">
                {/* <h3>Top Performers</h3> */}
                <div className="performerCategories">
                  {/* Goals */}
                  <div className="performerCategory">
                    <h4>Goals</h4>
                    {topPerformers.goals?.length > 0 ? (
                      topPerformers.goals.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.goals}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.goals}</span>
                    </div>
                  </div>

                  {/* Assists */}
                  <div className="performerCategory">
                    <h4>Assists</h4>
                    {topPerformers.assists?.length > 0 ? (
                      topPerformers.assists.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.assists}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.assists}</span>
                    </div>
                  </div>

                  {/* Total Shots */}
                  <div className="performerCategory">
                    <h4>Total Shots</h4>
                    {topPerformers.shotAttempts?.length > 0 ? (
                      topPerformers.shotAttempts.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.shotAttempts}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.shotAttempts}</span>
                    </div>
                  </div>

                  {/* Scoring Accuracy */}
                  <div className="performerCategory">
                    <h4>Scoring Accuracy</h4>
                    <p>(Goals / Total Shots)</p>
                    {topPerformers.scoringAccuracy?.length > 0 ? (
                      topPerformers.scoringAccuracy.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">
                            {player.shotAttempts > 0
                              ? ((player.goals / player.shotAttempts) * 100).toFixed(1)
                              : '0.0'}%
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">
                        {(teamTotals.scoringAccuracy * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Shooting Accuracy */}
                  <div className="performerCategory">
                    <h4>Shooting Accuracy</h4>
                    <p>(Shots On Goal / Total Shots)</p>
                    {topPerformers.shootingAccuracy?.length > 0 ? (
                      topPerformers.shootingAccuracy.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">
                            {(player.shootingAccuracy * 100).toFixed(1)}%
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">
                        {(teamTotals.shootingAccuracy * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Ground Balls */}
                  <div className="performerCategory">
                    <h4>Ground Balls</h4>
                    {topPerformers.groundBall?.length > 0 ? (
                      topPerformers.groundBall.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.groundBall}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.groundBall}</span>
                    </div>
                  </div>

                  {/* Turnovers Caused */}
                  <div className="performerCategory">
                    <h4>Caused Turnovers</h4>
                    {topPerformers.causedTurnover?.length > 0 ? (
                      topPerformers.causedTurnover.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.causedTurnover}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.causedTurnover}</span>
                    </div>
                  </div>

                  {/* Faceoff Win % */}
                  <div className="performerCategory">
                    <h4>Faceoffs</h4>
                    <p>(Won / Attempts)</p>
                    {topPerformers.faceoffWinPct?.length > 0 ? (
                      topPerformers.faceoffWinPct.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{(player.winPct * 100).toFixed(1)}%</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{(teamTotals.faceoffWinPct * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Turnovers */}
                  <div className="performerCategory">
                    <h4>Turnovers</h4>
                    {topPerformers.turnOver?.length > 0 ? (
                      topPerformers.turnOver.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.turnOver}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.turnOver}</span>
                    </div>
                  </div>

                  {/* Clears */}
                  <div className="performerCategory">
                    <h4>Clears</h4>
                    <p>(Successful / Attempts)</p>
                    {topPerformers.clears?.length > 0 ? (
                      topPerformers.clears.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{(player.clearPct * 100).toFixed(1)}%</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{(teamTotals.clearPct * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Shots On Goal */}
                  {/* <div className="performerCategory">
                    <h4>Shots On Goal</h4>
                    {topPerformers.shotOG?.length > 0 ? (
                      topPerformers.shotOG.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{player.shotOG}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{teamTotals.shotOG}</span>
                    </div>
                  </div> */}

                  <div className="performerCategory">
                    <h4>Penalties</h4>
                    {topPerformers.penalties?.length > 0 ? (
                      topPerformers.penalties.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {player.player}</span>
                          <span className="statValue">{player.count}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{topPerformers.teamTotals?.penalties || 0}</span>
                    </div>
                  </div>

                  <div className="performerCategory">
                    <h4>Penalty Time</h4>
                    {topPerformers.penaltyTime?.length > 0 ? (
                      topPerformers.penaltyTime.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {player.player}</span>
                          <span className="statValue">{formatMinutesToMMSS(player.totalTime)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue"><span className="statValue">{formatMinutesToMMSS(topPerformers.teamTotals?.penaltyTime || 0)}</span></span>
                    </div>
                  </div>


                  {/* Saves */}
                  <div className="performerCategory">
                    <h4>Saves</h4>
                    <p>(Saves / Shots On Goal)</p>
                    {topPerformers.saves?.length > 0 ? (
                      topPerformers.saves.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{(player.savePct * 100).toFixed(1)}%</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{(teamTotals.savePct * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Saves Controlled */}
                  <div className="performerCategory">
                    <h4>Saves Controlled</h4>
                    <p>(Controlled / Saves)</p>
                    {topPerformers.savesControlled?.length > 0 ? (
                      topPerformers.savesControlled.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{(player.controlledPct * 100).toFixed(1)}%</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{(teamTotals.controlledPct * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Saves Not-Controlled */}
                  <div className="performerCategory">
                    <h4>Saves Not-Controlled</h4>
                    <p>(Not Controlled / Saves)</p>
                    {topPerformers.savesNotControlled?.length > 0 ? (
                      topPerformers.savesNotControlled.map((player, index) => (
                        <div key={index} className="performerItem">
                          <span className="playerName">#{player.jersey} {formatPlayerName(player.name)}</span>
                          <span className="statValue">{(player.notControlledPct * 100).toFixed(1)}%</span>
                        </div>
                      ))
                    ) : (
                      <div className="noData">No data available</div>
                    )}
                    <div className="performerItem teamTotal">
                      <span className="playerName"><strong>Team Total</strong></span>
                      <span className="statValue">{(teamTotals.notControlledPct * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

              </div>
            </>
          ) : (
            <div className="noData">No player statistics available</div>
          )}
        </div>

        <div className="dashboardContent">
          <div className="recentGames">
            <h2>Recent Games</h2>
            {loading ? (
              <div className="loading">Loading games...</div>
            ) : gameData.length > 0 ? (
              <div className="gamesList">
                {gameData.slice(0, 4).map((game, index) => (
                  <div key={index} className="gameItem">
                    <div className="gameInfo">
                      <span className="opponent">
                        {game.awayTeam === operatorTeam?.teamId?.teamName
                          ? game.homeTeam
                          : game.awayTeam}
                      </span>
                      <span className="gameDate">
                        {new Date(game.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div
                      className={`gameResult ${game.gameResult === "Won"
                        ? "win"
                        : game.gameResult === "Lost"
                          ? "loss"
                          : game.gameResult === "Tied"
                            ? "tie"
                            : "pending"
                        }`}
                    >
                      {game.gameResult}
                    </div>
                  </div>
                ))}
              </div>

            ) : (
              <div className="noGames">No games recorded yet</div>
            )}
          </div>
          <div className="recentGames">
            <StatsChart
              gamesPlayed={countData.totalGames || 0}
              wins={countData.wins || 0}
              losses={countData.losses || 0}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;